import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import DialPhone, { PhoneUserIdentity } from '../../connection/phone';
import { SendDtmf, HangupPhone, HangupPhoneByUser } from '../../connection/phone';
import { GreenroomState } from '../../stores/types/types';
import {
	COUNTRY_CODE_OPTIONS,
	DEFAULT_COUNTRY_CODE,
	buildE164,
	parsePhoneNumber,
	validateLocalNumber,
} from '../../utils/phone-validation';

type DialStatus = 'idle' | 'dialing' | 'connected' | 'error';

interface Props {
	identity: PhoneUserIdentity & { first_name?: string; last_name?: string; phone?: string };
	onClose: () => void;
	onCallStarted?: (sipConnectionId: string) => void;
	onHangup?: () => void;
	isCallConnected?: boolean;
	activeSipConnectionId?: string | null;
}

const PAD_ROWS = [
	[{ key: '1', sub: '' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' }],
	[{ key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' }],
	[{ key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' }],
	[{ key: '*', sub: '' }, { key: '0', sub: '+' }, { key: '#', sub: '' }],
];

export default function FeedPhoneDialer({ identity, onClose, onCallStarted, onHangup, isCallConnected, activeSipConnectionId }: Props) {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meetingId = useSelector(
		(state: GreenroomState) => state.SessionReducer.meeting?.meeting_id,
	);

	const activeMasks = useSelector(
		(state: GreenroomState) => state.MasksReducer.activeMasksEntity?.entityValue,
	);
	const profileMasks = useSelector((state: GreenroomState) => state.MasksReducer.profileMasks);
	const resolveMaskPhone = () => {
		if (!identity.email) return undefined;
		const maskId = activeMasks?.[identity.email];
		return maskId != null ? profileMasks[maskId]?.phone ?? undefined : undefined;
	};

	// Parse the initial phone into country code + local number.
	const initialPhone = resolveMaskPhone() || identity.phone || '';
	const parsed = useMemo(() => {
		if (!initialPhone) return { countryCode: DEFAULT_COUNTRY_CODE, localNumber: '' };
		return parsePhoneNumber(initialPhone);
	}, [initialPhone]);

	const [countryCode, setCountryCode] = useState(parsed.countryCode);
	const [localNumber, setLocalNumber] = useState(parsed.localNumber);
	const [status, setStatus] = useState<DialStatus>(() => isCallConnected ? 'connected' : 'idle');
	const [errorMessage, setErrorMessage] = useState('');
	const touched = useRef(false);

	const displayName =
		[identity.first_name, identity.last_name].filter(Boolean).join(' ') ||
		identity.email ||
		'Unknown';

	const initials = (
		[identity.first_name?.[0], identity.last_name?.[0]].filter(Boolean).join('') ||
		identity.email?.[0] ||
		'?'
	).toUpperCase();

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				if (status === 'dialing' && activeSipConnectionId && token && meetingId) {
					HangupPhone(token, meetingId, activeSipConnectionId).catch(() => {});
					onHangup?.();
				}
				onClose();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose, status, activeSipConnectionId, token, meetingId, onHangup]);

	// Populate from mask data whenever Redux updates.
	useEffect(() => {
		if (touched.current) return;
		const phone = resolveMaskPhone() || identity.phone || '';
		if (phone) {
			const p = parsePhoneNumber(phone);
			setCountryCode(p.countryCode);
			setLocalNumber(p.localNumber);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeMasks, profileMasks, identity.email, identity.phone]);

	useEffect(() => {
		if (isCallConnected) {
			if (status === 'dialing' || status === 'idle') {
				setStatus('connected');
			}
		} else if (status === 'connected') {
			setStatus('idle');
		}
	}, [isCallConnected, status]);

	// Auto-hangup if the call isn't answered within 60 seconds.
	useEffect(() => {
		if (status !== 'dialing') return;
		const timer = setTimeout(() => {
			if (!token || !meetingId) return;
			setErrorMessage('Call timed out — no answer');
			setStatus('error');
			if (activeSipConnectionId) {
				HangupPhone(token, meetingId, activeSipConnectionId);
			}
			onHangup?.();
		}, 60000);
		return () => clearTimeout(timer);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const appendDigit = useCallback((digit: string) => {
		touched.current = true;
		setLocalNumber((prev) => prev + digit);
	}, []);

	const backspace = useCallback(() => {
		touched.current = true;
		setLocalNumber((prev) => prev.slice(0, -1));
	}, []);

	const validation = useMemo(() => validateLocalNumber(localNumber, countryCode), [localNumber, countryCode]);

	const handleDial = async () => {
		if (!validation.valid || !token || !meetingId || status === 'dialing') return;
		setStatus('dialing');
		setErrorMessage('');
		const fullNumber = buildE164(countryCode, localNumber);
		const result = await DialPhone(token, fullNumber, meetingId, identity);
		if (result?.message) {
			setStatus('error');
			setErrorMessage(result.message);
		} else {
			onCallStarted?.(result?.sipCall?.connectionId ?? '');
		}
	};

	const handleDtmfKey = useCallback(async (digit: string) => {
		if (!token || !meetingId || !activeSipConnectionId) return;
		await SendDtmf(token, meetingId, activeSipConnectionId, digit);
	}, [token, meetingId, activeSipConnectionId]);

	const handleEndCall = async () => {
		if (!token || !meetingId) return;
		let result: { ok: boolean; message?: string };
		if (activeSipConnectionId) {
			result = await HangupPhone(token, meetingId, activeSipConnectionId);
		} else if (identity.gr_user) {
			result = await HangupPhoneByUser(token, meetingId, identity.gr_user);
		} else {
			setStatus('error');
			setErrorMessage('Cannot end call: connection ID unavailable.');
			return;
		}
		if (!result.ok) {
			setErrorMessage(result.message ?? 'Failed to end call');
			return;
		}
		onHangup?.();
		onClose();
	};

	const isConnected = status === 'connected';

	const handleDismiss = useCallback(async () => {
		if (status === 'dialing' && activeSipConnectionId && token && meetingId) {
			await HangupPhone(token, meetingId, activeSipConnectionId).catch(() => {});
			onHangup?.();
		}
		onClose();
	}, [status, activeSipConnectionId, token, meetingId, onClose, onHangup]);

	return ReactDOM.createPortal(
		<div className="phone-dialer-modal-backdrop" onClick={handleDismiss}>
			<div className="phone-dialer-modal" onClick={(e) => e.stopPropagation()}>

				<div className="phone-dialer-modal__header">
					<div className="phone-dialer-modal__avatar">
						{identity.poster
							? <img src={identity.poster} alt={displayName} />
							: <span>{initials}</span>
						}
					</div>
					<div className="phone-dialer-modal__user-info">
						<span className="phone-dialer-modal__name">{displayName}</span>
						{isConnected
							? <span className="phone-dialer-modal__status phone-dialer-modal__status--connected">Connected</span>
							: status === 'dialing'
								? <span className="phone-dialer-modal__status phone-dialer-modal__status--connecting">Connecting…</span>
								: identity.email && <span className="phone-dialer-modal__email">{identity.email}</span>
						}
					</div>
				</div>

				{!isConnected && (
					<div className="phone-dialer-modal__screen">
						<select
							className="phone-dialer-modal__country-select"
							value={countryCode}
							onChange={(e) => { touched.current = true; setCountryCode(e.target.value); }}
							disabled={status === 'dialing'}
						>
							{COUNTRY_CODE_OPTIONS.map((entry) => (
								<option key={`${entry.code}-${entry.country}`} value={entry.code}>
									{entry.country} (+{entry.code})
								</option>
							))}
						</select>
						<div className="phone-dialer-modal__number-row">
							<span className="phone-dialer-modal__code-prefix">+{countryCode}</span>
							<input
								className="phone-dialer-modal__number-input"
								type="tel"
								placeholder="555 000 0000"
								value={localNumber}
								onChange={(e) => { touched.current = true; setLocalNumber(e.target.value); }}
								disabled={status === 'dialing'}
								autoFocus
							/>
							{localNumber && (
								<button
									className="phone-dialer-modal__backspace"
									onClick={backspace}
									type="button"
									tabIndex={-1}
								>
									⌫
								</button>
							)}
						</div>
						{localNumber && !validation.valid && validation.error && (
							<span className="phone-dialer-modal__validation-error">{validation.error}</span>
						)}
					</div>
				)}

				<div className="phone-dialer-modal__pad">
					{PAD_ROWS.map((row, i) => (
						<div key={i} className="phone-dialer-modal__pad-row">
							{row.map(({ key, sub }) => (
								<button
									key={key}
									className="phone-dialer-modal__pad-key"
									onClick={() => isConnected ? handleDtmfKey(key) : appendDigit(key)}
									type="button"
									tabIndex={-1}
								>
									<span className="phone-dialer-modal__pad-digit">{key}</span>
									{sub && <span className="phone-dialer-modal__pad-sub">{sub}</span>}
								</button>
							))}
						</div>
					))}
				</div>

				{errorMessage && (
					<span className="phone-dialer-modal__error">{errorMessage}</span>
				)}

				<div className="phone-dialer-modal__actions">
					{isConnected ? (
						<>
							<button className="phone-dialer-modal__cancel" onClick={onClose} type="button">
								Minimize
							</button>
							<button className="phone-dialer-modal__hangup" onClick={handleEndCall} type="button">
								End Call
							</button>
						</>
					) : (
						<>
							<button className="phone-dialer-modal__cancel" onClick={handleDismiss} type="button">
								Cancel
							</button>
							<button
								className="phone-dialer-modal__call"
								onClick={handleDial}
								disabled={status === 'dialing' || !validation.valid}
								type="button"
							>
								{status === 'dialing' ? 'Calling…' : 'Call'}
							</button>
						</>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
